import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  MenuItem,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/store";
import { fetchUsers, User } from "src/features/administrationSlice";
import { getUserDisplayName, getUserAvatar, getUserId } from "src/features/authSlice";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useTranslation } from "react-i18next";

const roles = ["superAdmins", "admins", "teamMembers"];

const RoleChipSelect = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { users } = useSelector((state: RootState) => state.admin);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([
    "admins",
    "superAdmins",
  ]);
  const [selectedOwnerships, setSelectedOwnerships] = useState<User | null>(
    null
  );
  const { user: loggedInUser } = useSelector((state: RootState) => state.auth);
  const [selectedFollowers, setSelectedFollowers] = useState<string[]>([]);
  const [anchorElRoles, setAnchorElRoles] = useState<null | HTMLElement>(null);
  const [anchorElFollowers, setAnchorElFollowers] =
    useState<null | HTMLElement>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [ownershipDialogOpen, setOwnershipDialogOpen] =
    useState<boolean>(false);
  const [newOwnership, setNewOwnership] = useState<{ title: string } | null>(
    null
  );

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      superAdmins: t("positions.roleChip.roles.superAdmins"),
      admins: t("positions.roleChip.roles.admins"),
      teamMembers: t("positions.roleChip.roles.teamMembers"),
    };

    return labels[role] || role;
  };

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    setSelectedOwnerships({
      name: getUserDisplayName(loggedInUser),
      avatar: getUserAvatar(loggedInUser) || "",
      id: getUserId(loggedInUser) || "",
      email: loggedInUser?.email || "",
    });
  }, [loggedInUser]);

  const handleRoleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElRoles(event.currentTarget);
  };

  const handleRoleClose = () => {
    setAnchorElRoles(null);
  };

  const handleFollowerClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFollowers(event.currentTarget);
  };

  const handleFollowerClose = () => {
    setAnchorElFollowers(null);
  };

  const handleRoleSelect = (role: string) => {
    if (!selectedRoles.includes(role)) {
      setSelectedRoles([...selectedRoles, role]);
    }
    setAnchorElRoles(null);
  };

  const handleFollowerSelect = (userId: string) => {
    if (!selectedFollowers.includes(userId)) {
      setSelectedFollowers([...selectedFollowers, userId]);
    }
    setAnchorElFollowers(null);
  };

  const handleRoleDelete = (roleToDelete: string) => () => {
    setSelectedRoles((roles) => roles.filter((role) => role !== roleToDelete));
  };

  const handleFollowerDelete = (followerToDelete: string) => () => {
    setSelectedFollowers((followers) =>
      followers.filter((follower) => follower !== followerToDelete)
    );
  };

  const handleSaveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmContinue = () => {
    if (newOwnership) {
      const selectedUser = users.find(
        (user) => user.name === newOwnership.title
      );
      setSelectedOwnerships(selectedUser || null);
    }

    setConfirmDialogOpen(false);
    setOwnershipDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const handleOwnershipClick = () => {
    setOwnershipDialogOpen(true);
  };

  const handleOwnershipChange = (
    event: any,
    value: { title: string } | null
  ) => {
    setNewOwnership(value);
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        width: "100%",
        height: "100%",
        borderRadius: "5px",
        p: 2,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {t("positions.roleChip.ownership")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
          {selectedOwnerships && (
            <Chip
              key={selectedOwnerships.id}
              label={selectedOwnerships.name}
              avatar={
                <Avatar
                  sx={{ width: 24, height: 24, mr: 1 }}
                  src={selectedOwnerships.avatar}
                />
              }
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                borderColor: "grey.300",
              }}
            />
          )}
        </Box>

        <Button
          startIcon={<AutorenewIcon />}
          size="small"
          sx={{ textTransform: "none" }}
          onClick={handleOwnershipClick}
        >
          {t("positions.changeOwnership")}
        </Button>
      </Box>

      {/* Access (Roles) Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {t("positions.roleChip.access")}
        </Typography>

        <Menu
          anchorEl={anchorElRoles}
          open={Boolean(anchorElRoles)}
          onClose={handleRoleClose}
        >
          {roles.map((role) => (
            <MenuItem key={role} onClick={() => handleRoleSelect(role)}>
              {getRoleLabel(role)}
            </MenuItem>
          ))}
          {users.map((user: User) => (
            <MenuItem key={user.id} onClick={() => handleRoleSelect(user.id)}>
              <Avatar
                src={user.avatar}
                sx={{ bgcolor: "grey.300", width: 24, height: 24, mr: 1 }}
              />
              <Typography> {user.name}</Typography>
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
          {selectedRoles.map((role) => {
            const follower = users.find((user) => user.id === role);
            return follower ? (
              <Chip
                key={follower.id}
                label={follower.name}
                onDelete={handleFollowerDelete(follower.id)}
                avatar={
                  <Avatar
                    src={follower.avatar}
                    sx={{ bgcolor: "grey.300", width: 24, height: 24 }}
                  />
                }
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                  borderColor: "grey.300",
                }}
              />
            ) : (
              <Chip
                key={role}
                label={getRoleLabel(role)}
                onDelete={handleRoleDelete(role)}
                avatar={
                  <Avatar sx={{ bgcolor: "grey.300", width: 24, height: 24 }} />
                }
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                  borderColor: "grey.300",
                }}
              />
            );
          })}
        </Box>

        <Button
          startIcon={<AddIcon />}
          size="small"
          sx={{ textTransform: "none" }}
          onClick={handleRoleClick}
        >
          {t("positions.roleChip.addRole")}
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {t("positions.roleChip.followers")}
        </Typography>

        <Menu
          anchorEl={anchorElFollowers}
          open={Boolean(anchorElFollowers)}
          onClose={handleFollowerClose}
        >
          {users.map((user: User) => (
            <MenuItem
              key={user.id}
              onClick={() => handleFollowerSelect(user.id)}
            >
              <Avatar
                src={user.avatar}
                sx={{ bgcolor: "grey.300", width: 24, height: 24, mr: 1 }}
              />
              <Typography> {user.name}</Typography>
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
          {selectedFollowers.map((followerId) => {
            const follower = users.find((user) => user.id === followerId);
            return (
              follower && (
                <Chip
                  key={follower.id}
                  label={follower.name}
                  onDelete={handleFollowerDelete(follower.id)}
                  avatar={
                    <Avatar
                      src={follower.avatar}
                      sx={{ bgcolor: "grey.300", width: 24, height: 24 }}
                    />
                  }
                  sx={{
                    fontWeight: "bold",
                    color: "text.primary",
                    borderColor: "grey.300",
                  }}
                />
              )
            );
          })}
        </Box>

        <Button
          startIcon={<AddIcon />}
          size="small"
          sx={{ textTransform: "none" }}
          onClick={handleFollowerClick}
        >
          {t("positions.roleChip.addFollowers")}
        </Button>
      </Box>

      <Dialog
        open={ownershipDialogOpen}
        onClose={() => setOwnershipDialogOpen(false)}
        aria-labelledby="ownership-dialog"
      >
        <DialogTitle id="ownership-dialog">{t("positions.roleChip.selectNewOwnership")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={users.map((user) => ({ title: user.name })) ?? []}
            sx={{ width: 300, zIndex: 3434 }}
            onChange={handleOwnershipChange}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              if (option.title) {
                return option.title;
              }
              return option.title;
            }}
            renderInput={(params) => (
              <TextField sx={{ mt: 2 }} {...params} label={t("positions.changeOwnershipDialog.newOwner")} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnershipDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!newOwnership}
            onClick={handleSaveClick}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmCancel}
        aria-labelledby="confirm-dialog"
      >
        <DialogTitle id="confirm-dialog">{t("confirmDialog.title")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("positions.roleChip.confirmOwnershipChange")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} color="primary">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmContinue}
            color="primary"
            variant="contained"
          >
            {t("common.continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleChipSelect;
